import React, { memo } from 'react';
import Image from 'next/image';
import { TDropdownList } from './DropdownComponent';
import { PRODUCT_OPTION } from './DropdownData';

type DropdownProps = {
  activeState: boolean;
  logoutFunction?: () => void;
  dropdownData: Record<string, string>;
};

type GeneralDropdownProps = {
  activeState: boolean;
  logoutFunction?: () => void;
  dropdownList?: TDropdownList;
  language?: string;
};

export type ChildProps = {
  id: string;
  url: string;
  icon: string;
  title: string;
  desc: string;
};

type MoreDropdownProps = {
  activeState: boolean;
  logoutFunction?: () => void;
};

type TElement = {
  id: string;
  name: string;
  link?: string;
  icon?: string;
  child?: TElement[];
};

export const SingleDropDown = memo(function SingleDropDown(
  props: DropdownProps
): JSX.Element {
  return (
    <div
      className={` ${
        props.activeState ? '' : 'hidden'
      } origin-top-right absolute z-10 left-5 w-56 focus:outline-none sm:relative sm:w-full sm:left-0 sm:px-2 sm:overflow-y-scroll max-h-viewport-75`}
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
      tabIndex={-1}
    >
      <div
        className="p-2 left-5 w-56 rounded-md shadow-lg bg-white sm:shadow-none sm:rounded-none"
        role="none"
      >
        {props.dropdownData && props.dropdownData.length
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (props?.dropdownData as unknown as Array<any>)?.map(
              (ele: TElement) => (
                <div key={ele.name}>
                  <NavLink ele={ele} />
                  {ele.child
                    ? ele.child.map((item: TElement) => (
                        <div key={item.name} className="px-2">
                          <NavLink ele={item} />
                        </div>
                      ))
                    : null}
                </div>
              )
            )
          : null}
      </div>
    </div>
  );
});

const NavLink = memo(function NavLink({ ele }: { ele: TElement }): JSX.Element {
  return (
    <div className="flex items-center pl-2 hover:bg-blue-50 rounded-md">
      {ele.icon && (
        <div className="w-2/12">
          <Image
            src={ele.icon}
            height="24"
            width="24"
            alt={ele.name}
            objectFit={'contain'}
          />
        </div>
      )}
      <div className="">
        {ele.link ? (
          <a
            href={ele.link}
            target="_blank"
            className="text-gray-700 block px-4 py-3 font-md text-sm rounded-md hover:bg-blue-50  sm:hover:bg-grey-150 sm:p-2 sm:text-s-14"
            role="menuitem"
            tabIndex={-1}
            rel="noreferrer"
          >
            {ele.name}
          </a>
        ) : (
          <div className="py-1 pl-2 text-font-200">{ele.name}</div>
        )}
      </div>
    </div>
  );
});

export const ProductDropDown = memo(function ProductDropDown(
  props: DropdownProps
): JSX.Element {
  const { dropdownData } = props;

  if (!dropdownData) return <div></div>;
  const dropDownLen = (dropdownData?.length as unknown as number) || 0;
  return (
    <div
      className={` ${props.activeState ? '' : 'hidden'} ${
        dropDownLen > 2 ? 'w-10/12' : 'w-8/12'
      }  origin-top-right rounded-md shadow-md sm:shadow-none absolute z-10 left-24 pt-2 focus:outline-none  overflow-y-scroll sm:hidden max-h-viewport-75`}
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
      tabIndex={-1}
    >
      <div className="bg-white">
        <div className="flex px-12 py-4">
          {dropdownData && dropDownLen
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (dropdownData as unknown as Array<any>)?.map(
                (ele: {
                  name: string;
                  seeAllButton: {
                    id: string;
                    url: string;
                    title: string;
                  };
                  child: Array<{
                    title: string;
                    url: string;
                    icon?: string;
                    id: string;
                  }>;
                }) => (
                  <div
                    className={`${'w-1/' + dropDownLen} px-4 sm:w-full `}
                    key={ele.name}
                  >
                    <div className="w-full">
                      <h6 className="text-base font-semibold text-font-200 py-2 items-center mt-4 mb-2">
                        {ele.name.toUpperCase()}
                      </h6>
                      <hr className="bottom-1" />
                    </div>
                    {ele.child.map((item) => (
                      <a
                        key={item.title}
                        data-label={item.title}
                        target="_blank"
                        href={item.url}
                        referrerPolicy="no-referrer-when-downgrade"
                        rel="noreferrer"
                      >
                        <div className="flex items-center py-3 pl-4 hover:bg-blue-50 rounded-md">
                          {item.icon && (
                            <div className="w-2/12">
                              <Image
                                src={item.icon}
                                height="24"
                                width="24"
                                alt={item.title}
                                objectFit={'contain'}
                              />
                            </div>
                          )}
                          <div className="w-10/12">
                            <p className="text-base text-font-500 font-md">
                              {item.title}
                            </p>
                          </div>
                        </div>
                      </a>
                    ))}
                    {ele.seeAllButton ? (
                      <div className="flex pl-4 pb-6">
                        <div className="w-2/12"></div>
                        <div className="w-10/12">
                          <a
                            data-label={ele.name}
                            className="btn-link items-center font-bold text-s-14 sm:pl-4 sm:pr-0 sm:py-0"
                            href={`${ele.seeAllButton.url}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {ele.seeAllButton.title}
                            <span className="font-medium">&nbsp; →</span>
                          </a>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )
              )
            : null}
        </div>
      </div>
    </div>
  );
});

export const OldProductDropDown = memo(function OldProductDropDown(
  props: MoreDropdownProps
): JSX.Element {
  return (
    <div
      className={` ${
        props.activeState ? '' : 'hidden'
      } origin-top-right rounded-md shadow-md sm:shadow-none absolute z-10 left-24 pt-2 focus:outline-none overflow-hidden w-10/12 sm:overflow-y-scroll sm:hidden max-h-viewport-75`}
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
      tabIndex={-1}
    >
      <div className="bg-white">
        <div className="flex flex-wrap wg-container nav-menu-sticky nav-menu-container px-4">
          <div className="w-1/3 pl-8 flex py-2 items-center mt-6 mb-2">
            <div className="w-full">
              <h6 className="text-base font-semibold text-font-200">
                ENTERPRISE
              </h6>
            </div>
          </div>
          <div className="w-1/3 pl-8 flex py-2 items-center mt-6 mb-2">
            <div className="w-full">
              <h6 className="text-base font-semibold text-font-200">
                INDIVIDUALS
              </h6>
            </div>
          </div>
          <div className="w-1/3 pl-8 flex py-2 items-center mt-6 mb-2">
            <div className="w-full">
              <h6 className="text-base font-semibold text-font-200">
                MORE PRODUCTS
              </h6>
            </div>
          </div>
        </div>
        <hr className="bottom-1" />
        <div className="flex  wg-container px-4 py-4">
          {PRODUCT_OPTION.map(
            (ele: {
              name: string;
              child: Array<{
                title: string;
                url: string;
                icon?: string;
                id: string;
              }>;
            }) => (
              <div className="w-1/3 px-4" key={ele.name}>
                {ele.child.map((item) => (
                  <a
                    key={item.title}
                    data-label={item.title}
                    target="_blank"
                    href={item.url}
                    referrerPolicy="no-referrer-when-downgrade"
                    rel="noreferrer"
                  >
                    <div className="flex items-center py-4 pl-4 ">
                      {item.icon && (
                        <div className="w-2/12">
                          <Image
                            src={item.icon}
                            height="24"
                            width="24"
                            alt={item.title}
                            objectFit={'contain'}
                          />
                        </div>
                      )}
                      <div className="w-10/12">
                        <p className="text-base text-font-500 font-md">
                          {item.title}
                        </p>
                      </div>
                    </div>
                  </a>
                ))}
                <div className="flex pl-4 pb-6">
                  <div className="w-2/12"></div>
                  <div className="w-10/12">
                    <a
                      data-label={ele.name}
                      className="btn-link items-center font-bold text-s-14 sm:pl-4 sm:pr-0 sm:py-0"
                      href="https://cleartax.in/you"
                      target="_blank"
                      rel="noreferrer"
                    >
                      See all<span className="font-medium">&nbsp; →</span>
                    </a>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
});

export const GeneralDropdown = memo(function GeneralDropdown({
  activeState,
  dropdownList,
  language = 'English',
}: GeneralDropdownProps): JSX.Element {
  return (
    <div
      className={` ${
        activeState ? '' : 'hidden'
      } origin-top-right absolute z-10 left-5 pt-2 w-56 focus:outline-none sm:relative sm:w-full sm:left-0 sm:pl-8 sm:overflow-y-scroll max-h-viewport-75`}
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
      tabIndex={-1}
    >
      <div
        className="py-1 rounded-md shadow-lg   sm:shadow-none bg-white"
        role="none"
      >
        {dropdownList &&
          dropdownList.map(
            (ele: { name: string; name_ar?: string; link: string }) => (
              <a
                href={ele.link}
                target="_blank"
                className="text-gray-700 block px-4 py-3 font-md text-sm hover:bg-blue-50 sm:hover:bg-white"
                role="menuitem"
                tabIndex={-1}
                key={ele.name}
                id={`menu-item-${ele.name}`}
                rel="noreferrer"
              >
                {language === 'Arabic' ? ele.name_ar : ele.name}
              </a>
            )
          )}
      </div>
    </div>
  );
});
